// Fix prettier/eslint conflicts for the opening brace after export class...
/* eslint-disable brace-style */
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { CoreActions } from '@next/core-lib';
import { AbstractDialog, AbstractDialogResultData, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { TableColumnTypes, TableConfig, TableStickyTypes } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import {
  Subject,
  finalize,
  takeUntil,
  BehaviorSubject,
  Observable,
  combineLatest,
  EMPTY,
} from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UnitOfMeasurementEnum } from 'src/app/crop-aid/crop-aid.model';
import {
  VZCTransportStatus,
  SewageSludgeCompostTransports,
  SewageSludgeCompostTransport,
  SewageSludgeCompostTransportResponseCropAid,
  VZCTransportType,
  GetSewageSludgeCompostTransportsResponse,
  GetSewageSludgeCompostTransport,
} from 'src/app/fertilizers/models/sewage-sludge-compost-transport.model';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { CustomManureAbstractDialogData } from 'src/app/shared/components/dialog/abstract-dialog';
import { AppState } from 'src/app/shared/models';

import { ManureSpaceApiService } from '../../../../manure-space/services/manure-space-api.service';
import * as ManureSpaceSelectors from '../../../../manure-space/store/manure-space.selectors';
import { SalesOrderPeriod } from '../../../../sales-order-period/models/sales-order-period.models';
import { SewageSludgeCompostTransportService } from '../../../../shared/services/sewage-sludge-compost-transport.service';
import { FertilizerImportTypeDialogComponent } from '../../../fertilizer-import-dialog/fertilizer-import-type-dialog/fertilizer-import-type-dialog.component';
import {
  DeleteFertilizersBySourceRequest,
  FertilizerRequest,
  FertilizerSource,
  FertilizerType,
} from '../../../models/fertilizer.model';
import * as FertilizersActions from '../../../store/actions';
import {
  SewageSludgeCompostEditTransportComponent,
  SewageSludgeCompostEditTransportData,
} from '../edit-transport-dialog/sewage-sludge-compost-edit-transport.component';

export interface SewageSludgeCompostTableRequestData extends CustomManureAbstractDialogData {
  kvkNumber: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-sewage-sludge-compost-transports-table',
  templateUrl: './sewage-sludge-compost-table.component.html',
  styleUrls: ['./sewage-sludge-compost-table.component.scss'],
  standalone: false,
})
export class SewageSludgeCompostTableComponent
  extends AbstractDialog<SewageSludgeCompostTableRequestData>
  implements OnInit, AfterViewInit, OnDestroy
{
  data = inject<SewageSludgeCompostTableRequestData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        SewageSludgeCompostTableComponent,
        AbstractDialogResultData<SewageSludgeCompostTableRequestData>
      >
    >(MatDialogRef);
  private readonly sewageSludgeCompostTransportService = inject(
    SewageSludgeCompostTransportService,
  );
  private readonly manureSpaceAPIService = inject(ManureSpaceApiService);
  private readonly locale = inject(LocaleService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly matDialog = inject(MatDialog);

  @ViewChild('periodIdCell') periodIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('statusCell') statusTemplateRef: TemplateRef<unknown>;

  private readonly destroyed$ = new Subject<boolean>();

  private manureSpaceId: number | null = null;

  private readonly sewageSludgeCompostTransportsSubject$ =
    new BehaviorSubject<SewageSludgeCompostTransports>({
      receivedVzcTransports: [],
      dispatchedVzcTransports: [],
    });

  private readonly mergedDispatchedAndReceivedTransports$: Observable<
    SewageSludgeCompostTransport[]
  > = this.sewageSludgeCompostTransportsSubject$.pipe(
    map((transports) => [
      ...transports.dispatchedVzcTransports,
      ...transports.receivedVzcTransports,
    ]),
  );

  protected readonly receivedTransportsTableConfig$: Observable<TableConfig> =
    this.sewageSludgeCompostTransportsSubject$.pipe(
      map((transports) => this.setTableConfig(transports.receivedVzcTransports)),
    );

  protected readonly dispatchedTransportsTableConfig$: Observable<TableConfig> =
    this.sewageSludgeCompostTransportsSubject$.pipe(
      map((transports) => this.setTableConfig(transports.dispatchedVzcTransports)),
    );

  private readonly salesOrderPeriods = new BehaviorSubject<SalesOrderPeriod[]>([]);

  private readonly salesOrderPeriods$: Observable<SalesOrderPeriod[]> = this.store.pipe(
    select(SalesOrderPeriodSelectors.selectSalesOrderPeriods),
    takeUntil(this.destroyed$),
  );

  private readonly hasInvalidManureCodes$: Observable<boolean> =
    this.mergedDispatchedAndReceivedTransports$.pipe(
      map((transports) => transports.some((transport) => !transport.cropAid?.id)),
    );

  private readonly importButtonDisabled$: Observable<boolean> = combineLatest([
    this.mergedDispatchedAndReceivedTransports$,
    this.hasInvalidManureCodes$,
  ]).pipe(
    map(([transports, hasInvalidManureCodes]) => transports.length === 0 || hasInvalidManureCodes),
  );

  private readonly dialogSubmitWarningMessage$ = combineLatest([
    this.mergedDispatchedAndReceivedTransports$,
    this.hasInvalidManureCodes$,
  ]).pipe(
    map(([transports, hasInvalidManureCodes]) =>
      this.generateErrorMessage(transports, hasInvalidManureCodes),
    ),
  );

  private readonly eligibleTransports$: Observable<FertilizerRequest[]> =
    this.mergedDispatchedAndReceivedTransports$.pipe(
      map((transports) =>
        transports
          .filter(
            (vzc) =>
              vzc.cropAid.id &&
              (vzc.status === VZCTransportStatus.Accepted ||
                vzc.status === VZCTransportStatus.InProgress),
          )
          .map((vzc) => this.mapSewageSludgeAndCompostToFertilizer(vzc, FertilizerType.Delivered)),
      ),
    );

  constructor() {
    super();

    this.salesOrderPeriods$.subscribe((periods) => this.salesOrderPeriods.next(periods));
    this.importButtonDisabled$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((disabled) => (this.data.disableConfirmButton = disabled));
    this.dialogSubmitWarningMessage$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((message) => (this.data.dialogSubmitWarningMessage = message));
    this.store
      .pipe(select(ManureSpaceSelectors.selectManureSpaceDetailsId), takeUntil(this.destroyed$))
      .subscribe((manureSpaceId) => {
        this.manureSpaceId = manureSpaceId;
      });
  }

  ngOnInit(): void {
    this.store.dispatch(CoreActions.setLoading({ loading: true }));
  }

  ngAfterViewInit(): void {
    this.sewageSludgeCompostTransportService
      .getSewageSludgeCompostTransports(
        this.data.kvkNumber,
        this.currentIsoDateString(this.data.startDate),
        this.currentIsoDateString(this.data.endDate),
      )
      .pipe(
        map(
          (response): SewageSludgeCompostTransports =>
            this.mapGetSewageSludgeCompostTransportsResponse(response),
        ),
        takeUntil(this.destroyed$),
        finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
      )
      .subscribe({
        next: (transports) => this.sewageSludgeCompostTransportsSubject$.next(transports),
        error: (error: HttpErrorResponse) =>
          this.dialog
            .notify({
              title: this.locale.translate('sharedTransport.table.errorImport'),
              text: this.generateGetSewageSludgeTransportsErrorMessage(error),
            })
            .pipe(() => EMPTY),
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.sewageSludgeCompostTransportsSubject$.complete();
  }

  protected confirmImportVzc(): void {
    this.eligibleTransports$.pipe(take(1)).subscribe((eligibleTransports) => {
      this.openOptionDialog(eligibleTransports);
    });
  }

  protected getPeriodName = (periodId: number): string =>
    this.salesOrderPeriods.value.find((p) => p.id === periodId)?.name;

  protected isTransportInvalid = (transport: SewageSludgeCompostTransport) =>
    !transport.cropAid || transport.periodId === 0;

  private generateErrorMessage(
    transports: SewageSludgeCompostTransport[],
    hasInvalidManureCodes: boolean,
  ): string | null {
    if (hasInvalidManureCodes) {
      return this.locale.translate('sewageSludgeAndCompost.table.importDisabledMessage', {
        manureCodes: this.generateListOfUnlinkableCropAids(transports),
      });
    }
    return null;
  }

  private currentIsoDateString(date: string): string {
    return new Date(+new Date(date) - new Date().getTimezoneOffset() * 60000).toISOString();
  }

  private setTableConfig(vzcTransports: SewageSludgeCompostTransport[]): TableConfig {
    return {
      dataSource: new MatTableDataSource<SewageSludgeCompostTransport>(vzcTransports),
      activeSort: { id: 'loadDate', start: 'desc', disableClear: true },
      columns: [
        {
          id: 'status',
          type: TableColumnTypes.TEMPLATE,
          disableSort: true,
          template: this.statusTemplateRef,
        },
        {
          id: 'cropAidName',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('sharedTransport.table.headers.fertilizer'),
          disableSort: true,
          className: 'flex__child--8',
          headerClassName: 'flex__child--8',
        },
        {
          id: 'comment',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('comments'),
          disableSort: true,
          className: 'flex__child--4',
          headerClassName: 'flex__child--4',
        },
        {
          id: 'periodId',
          type: TableColumnTypes.TEMPLATE,
          label: this.locale.translate('period'),
          disableSort: true,
          className: 'flex__child--3',
          headerClassName: 'flex__child--3',
          template: this.periodIdsTemplateRef,
        },
        {
          id: 'tonnage',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.amountInTon'),
          disableSort: true,
          className: 'flex__child--4',
          headerClassName: 'flex__child--4',
          digitsInfo: '1.1-1',
        },
        {
          id: 'nitrogenInKg',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.nitrogenInKg'),
          disableSort: true,
          className: 'flex__child--3',
          headerClassName: 'flex__child--3',
          digitsInfo: '1.0-0',
        },
        {
          id: 'phosphateInKg',
          type: TableColumnTypes.NUMBER,
          label: this.locale.translate('sharedTransport.table.headers.phosphateInKg'),
          disableSort: true,
          className: 'flex__child--3',
          headerClassName: 'flex__child--3',
          digitsInfo: '1.0-0',
        },
      ],
      actionColumn: true,
      actions: [
        {
          id: 'edit',
          label: this.locale.translate('edit'),
          callback: (transport: SewageSludgeCompostTransport) => {
            this.editTransport(transport);
          },
          icon: 'edit',
          disabled: () => false,
        },
        {
          id: 'delete',
          label: this.locale.translate('delete'),
          callback: (transport: SewageSludgeCompostTransport) => {
            this.removeTransport(transport.id);
          },
          icon: 'delete',
          disabled: () => false,
        },
      ],
      selectColumn: false,
      pagination: false,
      header: TableStickyTypes.STICKY,
      primaryColumn: ' ',
      alternateRows: true,
    };
  }

  private generateListOfUnlinkableCropAids(transports: SewageSludgeCompostTransport[]): string {
    return [
      ...new Set(
        transports
          .filter((transport) => !transport.cropAid?.id)
          .map((transport) => transport.manureCode),
      ),
    ].join(', ');
  }

  private mapSewageSludgeAndCompostToFertilizer(
    vzc: SewageSludgeCompostTransport,
    fertilizerType: FertilizerType,
  ): FertilizerRequest {
    return {
      cropAidId: vzc.cropAid.id,
      dosage: this.convertTonByCropAidUnitOfMeasurement(vzc.cropAid, vzc.tonnage),
      comment: vzc.comment,
      totalNitrogen: vzc.nitrogenInKg,
      totalPhosphate: vzc.phosphateInKg,
      periodId: vzc.periodId,
      fertilizerType:
        vzc.status === VZCTransportStatus.InProgress ? FertilizerType.NotDelivered : fertilizerType,
      fertilizerSource: FertilizerSource.ExternalVZC,
      manureCode: isNaN(Number(vzc.manureCode)) ? 0 : Number(vzc.manureCode),
    };
  }

  private generateComment = (
    vzc: GetSewageSludgeCompostTransport,
    transportType: VZCTransportType,
  ): string =>
    transportType === VZCTransportType.Received
      ? `${this.locale.translate('sharedTransport.table.headers.unloadDate')} ${vzc.unloadDate}`
      : `${this.locale.translate('sharedTransport.table.headers.loadDate')} ${vzc.loadDate}`;

  private matchDateToPeriodId(date: string): number {
    const period = this.salesOrderPeriods.value.find(
      (p) => p.periodNumber === new Date(date).getMonth() + 1,
    );
    return period ? period.id : 0;
  }

  private generateGetSewageSludgeTransportsErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 400) {
      switch (error.error) {
        case 'RELATION_NUMBER_NOT_FOUND':
          return this.locale.translate(
            'sharedTransport.table.joinDataRelationNumberNotFoundErrorMessage',
          );
        case 'MISSING_TRANSPORT_CERTIFICATIONS_MANDATE':
          return this.locale.translate(
            'sharedTransport.table.joinDataMissingTransportCertificationsMandateErrorMessage',
          );
        case 'NO_MANDATE':
        default:
          return this.locale.translate('sharedTransport.table.joinDataNoMandateErrorMessage');
      }
    }

    if (error.status === 404) {
      return this.locale.translate('sharedTransport.table.joinDataNoMandateErrorMessage');
    }

    return this.locale.translate('sewageSludgeAndCompost.table.genericErrorMessage');
  }

  private convertTonByCropAidUnitOfMeasurement(
    cropAid: SewageSludgeCompostTransportResponseCropAid,
    transportAmountInTon: number,
  ): number {
    if (cropAid) {
      switch (cropAid.unitOfMeasurement) {
        case UnitOfMeasurementEnum.KG:
          return transportAmountInTon * 1_000;
        case UnitOfMeasurementEnum.G:
          return transportAmountInTon * 1_000_000;
        default:
          return transportAmountInTon;
      }
    }

    return transportAmountInTon;
  }

  private mapGetSewageSludgeCompostTransportsResponse(
    response: GetSewageSludgeCompostTransportsResponse,
  ): SewageSludgeCompostTransports {
    const generateUniqueId = (): number => Math.floor(Math.random() * Date.now());
    const generatePeriodId = (transport: GetSewageSludgeCompostTransport): number =>
      transport.status === VZCTransportStatus.InProgress
        ? this.matchDateToPeriodId(transport.loadDate)
        : this.matchDateToPeriodId(transport.unloadDate);

    const mapTransport = (
      transport: GetSewageSludgeCompostTransport,
      transportType: VZCTransportType,
    ): SewageSludgeCompostTransport => ({
      id: generateUniqueId(),
      periodId: generatePeriodId(transport),
      comment: this.generateComment(transport, transportType),
      transportType,
      ...transport,
      cropAidName: transport.cropAid?.name ?? this.locale.translate('unknownFertilizer'),
    });

    return {
      receivedVzcTransports: response.receivedVzcTransports.map((t) =>
        mapTransport(t, VZCTransportType.Received),
      ),
      dispatchedVzcTransports: response.dispatchedVzcTransports.map((t) =>
        mapTransport(t, VZCTransportType.Dispatched),
      ),
    };
  }

  private editTransport(transport: SewageSludgeCompostTransport): void {
    const data: SewageSludgeCompostEditTransportData = {
      transport,
      title: this.locale.translate('sewageSludgeAndCompost.editTitle'),
    };

    const dialogRef = this.matDialog.open(SewageSludgeCompostEditTransportComponent, {
      id: 'fertilizer-import-dialog',
      data,
    });

    dialogRef
      .afterClosed()
      .subscribe((res: AbstractDialogResultData<SewageSludgeCompostEditTransportData>) => {
        if (res) {
          const result = res.result?.transport;
          if (result) {
            console.log(result);
            this.mutateFertilizersTable(result);
          }
        }
      });
  }

  private mutateFertilizersTable(transport: SewageSludgeCompostTransport) {
    const currentTransports = this.sewageSludgeCompostTransportsSubject$.value;

    currentTransports.receivedVzcTransports = currentTransports.receivedVzcTransports.map((t) =>
      t.id === transport.id ? transport : t,
    );
    currentTransports.dispatchedVzcTransports = currentTransports.dispatchedVzcTransports.map(
      (t) => (t.id === transport.id ? transport : t),
    );

    this.sewageSludgeCompostTransportsSubject$.next(currentTransports);
  }

  private removeTransport(transportId: number): void {
    const currentTransports = this.sewageSludgeCompostTransportsSubject$.value;

    currentTransports.receivedVzcTransports = currentTransports.receivedVzcTransports.filter(
      (transport) => transport.id !== transportId,
    );
    currentTransports.dispatchedVzcTransports = currentTransports.dispatchedVzcTransports.filter(
      (transport) => transport.id !== transportId,
    );

    this.sewageSludgeCompostTransportsSubject$.next(currentTransports);
  }

  private openOptionDialog(fertilizers: FertilizerRequest[]): void {
    this.dialog
      .responsiveDialog(FertilizerImportTypeDialogComponent, {
        text: this.locale.translate('animalManureTransport.importVZCQuestion'),
      })
      .subscribe(({ status, result }) => {
        if (status === 'CONFIRM') {
          if (result.result === 'overwrite') {
            this.deleteBeforeImport(fertilizers);
          } else if (result.result === 'add') {
            this.importWithoutDelete(fertilizers);
          }
        }
      });
  }

  private importWithoutDelete(fertilizers: FertilizerRequest[]) {
    this.store.dispatch(FertilizersActions.importMineralBalance({ fertilizers }));
    this.confirm();
  }

  private deleteBeforeImport(fertilizers: FertilizerRequest[]) {
    this.manureSpaceAPIService
      .deleteFertilizersBySource({
        manureSpaceId: this.manureSpaceId,
        fertilizerSource: FertilizerSource.ExternalVZC,
        fertilizerTypes: [
          FertilizerType.NotDelivered,
          FertilizerType.Delivered,
          FertilizerType.Dispatched,
        ],
      } as DeleteFertilizersBySourceRequest)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(FertilizersActions.importMineralBalance({ fertilizers }));
          this.confirm();
        },
        error: (_error) => {
          this.cancel();
          return this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: this.locale.translate(
                'animalManureTransport.table.GenericImportErrorMessage',
              ),
            }),
          );
        },
      });
  }
}
